/**
 * FinalProxyScoringSection — Section riêng trong /scoring cho Vòng Chung kết.
 *
 * Admin nhập phiếu chấm CK thay GK (workflow offline: GK chấm phiếu giấy tại
 * hội trường → admin nhập lên hệ thống). UI:
 *   - Liệt kê 2 finalists CK (NĐ + BĐ từ bracket CK) với badge vai trò
 *   - Mỗi đội có nút "Nhập phiếu chấm" → mở dialog
 *   - Dialog: chọn GK (dropdown existing + text input nếu GK mới) + 5 tiêu chí
 *     FINAL_RUBRIC + comments → submit qua POST /scoring/final-proxy-entry
 *   - Lịch sử phiếu đã nhập per team (đếm GK đã chấm + tổng điểm)
 *
 * Chỉ render khi current_round_id của case = round CK + admin role.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Add, Assessment, Gavel } from '@mui/icons-material'

import {
  useGetScoresQuery,
  useCreateFinalProxyEntryMutation,
  useUpdateFinalProxyEntryMutation,
} from '../store/api/scoringApi'
import { useGetLatestBracketQuery } from '../store/api/bracketsApi'
import { useGetUsersQuery } from '../store/api/usersApi'

// FINAL_RUBRIC mirror BE ScoringService.get_final_rubric (5 tiêu chí, 100đ)
const FINAL_CRITERIA: { key: string; name: string; description: string; max_score: number }[] = [
  {
    key: 'I_questioning',
    name: 'I. Kỹ năng hỏi (Questioning Skills)',
    description:
      'Câu hỏi ngắn gọn, trực diện, không mớm cung. Khai thác tình tiết chìa khóa. Biết điểm dừng.',
    max_score: 15,
  },
  {
    key: 'II_argument_quality',
    name: 'II. Chất lượng Bản luận cứ và Tranh luận',
    description:
      'Bao quát đủ 3 vấn đề (hôn nhân, nuôi con, tài sản). Lập luận chia tài sản xuất sắc.',
    max_score: 30,
  },
  {
    key: 'III_response_handling',
    name: 'III. Kỹ năng đối đáp và xử lý tình huống',
    description:
      'Trả lời trực diện câu hỏi hóc búa từ HĐXX; phản xạ tư duy nhanh, sắc bén.',
    max_score: 25,
  },
  {
    key: 'IV_legal_knowledge',
    name: 'IV. Kiến thức pháp lý (Legal Knowledge)',
    description:
      'Trích dẫn chuẩn xác Luật HN&GĐ, BLTTDS, BLDS. Hiểu rõ "tài sản chung hợp nhất".',
    max_score: 15,
  },
  {
    key: 'V_conduct_teamwork',
    name: 'V. Tác phong, ngôn ngữ và làm việc nhóm',
    description:
      'Ngôn ngữ cơ thể đĩnh đạc, từ ngữ pháp lý chuẩn mực; phân bổ thời gian hợp lý giữa 2 Luật sư.',
    max_score: 15,
  },
]
const TOTAL_MAX = FINAL_CRITERIA.reduce((s, c) => s + c.max_score, 0) // 100

interface Props {
  caseId: string
  finalRoundId: string
  teams: { id: string; name: string; university?: string | null }[]
}

export default function FinalProxyScoringSection({ caseId, finalRoundId, teams }: Props) {
  const { data: bracket } = useGetLatestBracketQuery(
    { case_id: caseId, round_id: finalRoundId },
    { skip: !caseId || !finalRoundId, pollingInterval: 30000 },
  )
  const { data: caseScores = [], refetch: refetchScores } = useGetScoresQuery(
    { case_id: caseId },
    { skip: !caseId, pollingInterval: 15000 },
  )
  const { data: allUsers = [] } = useGetUsersQuery()
  const judges = useMemo(
    () => allUsers.filter((u) => u.role === 'judge' || u.role === 'secretary'),
    [allUsers],
  )

  // 2 finalists từ bracket CK
  const finalists = useMemo(() => {
    const m = (bracket?.matches as any[] | undefined)?.[0]
    if (!m) return [] as Array<{ team_id: string; role: 'plaintiff' | 'defendant' }>
    return [
      { team_id: m.team_plaintiff_id, role: 'plaintiff' as const },
      { team_id: m.team_defendant_id, role: 'defendant' as const },
    ].filter((x) => !!x.team_id)
  }, [bracket])

  // Lookup scores theo team (chỉ tính CK round + submitted)
  const scoresByTeam = useMemo(() => {
    const m = new Map<string, any[]>()
    for (const s of caseScores as any[]) {
      if (s.round_id !== finalRoundId) continue
      if (s.status === 'draft') continue
      const arr = m.get(s.team_id) || []
      arr.push(s)
      m.set(s.team_id, arr)
    }
    return m
  }, [caseScores, finalRoundId])

  // Dialog state — support cả create (mode='create') + edit (mode='edit' với score)
  const [dialog, setDialog] = useState<{
    open: boolean
    teamId: string
    teamName: string
    role: string
    editScore: any | null
  }>({ open: false, teamId: '', teamName: '', role: '', editScore: null })

  if (!finalRoundId) return null
  if (!bracket || finalists.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Gavel color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Vòng Chung kết — Nhập phiếu chấm (admin)
          </Typography>
        </Stack>
        <Alert severity="info">
          Chưa có bracket Chung kết. BTC cần bấm "Công bố chính thức Vòng Bán
          kết → Chung kết" trước khi nhập phiếu chấm.
        </Alert>
      </Paper>
    )
  }

  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'primary.main', borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
          <Gavel color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Vòng Chung kết — Nhập phiếu chấm (admin)
          </Typography>
          <Chip size="small" color="warning" label={`Tổng: ${TOTAL_MAX}đ / phiếu`} sx={{ fontWeight: 600 }} />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          GK chấm phiếu giấy tại hội trường → admin thu phiếu → nhập vào hệ
          thống tại đây. Mỗi phiếu submit ngay (lock luôn). Nhiều GK chấm
          cho 1 đội → tạo nhiều phiếu, ranking aggregate tổng.
        </Typography>
        <Grid container spacing={2}>
          {finalists.map((f) => {
            const team = teams.find((t) => t.id === f.team_id)
            const teamScores = scoresByTeam.get(f.team_id) || []
            const total = teamScores.reduce((sum, s) => sum + (Number(s.total_score) || 0), 0)
            return (
              <Grid item xs={12} md={6} key={f.team_id}>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: f.role === 'plaintiff' ? 'primary.main' : 'secondary.main', borderWidth: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={f.role === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn'}
                      color={f.role === 'plaintiff' ? 'primary' : 'secondary'}
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                    <Typography variant="body1" fontWeight={700}>
                      {team?.name || f.team_id}
                    </Typography>
                    {team?.university && (
                      <Typography variant="caption" color="text.secondary">
                        — {team.university}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Số phiếu đã nhập</Typography>
                      <Typography variant="h6" fontWeight={800} color="primary">
                        {teamScores.length}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Tổng điểm</Typography>
                      <Typography variant="h6" fontWeight={800}>
                        {total.toFixed(1)}
                      </Typography>
                    </Box>
                  </Stack>
                  {teamScores.length > 0 && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Lịch sử phiếu (click tên GK để sửa):
                      </Typography>
                      <Stack spacing={0.5}>
                        {teamScores.map((s) => {
                          const judgeName =
                            s.proxy_judge_name ||
                            judges.find((j) => j.id === s.delegating_judge_id)?.full_name ||
                            judges.find((j) => j.id === s.judge_id)?.full_name ||
                            (typeof s.judge_id === 'string' && s.judge_id.startsWith('proxy:')
                              ? s.judge_id.replace(/^proxy:/, '').replace(/_/g, ' ')
                              : s.judge_id?.slice(0, 8))
                          return (
                            <Box key={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                              <Button
                                variant="text"
                                size="small"
                                onClick={() =>
                                  setDialog({
                                    open: true,
                                    teamId: f.team_id,
                                    teamName: team?.name || f.team_id,
                                    role: f.role === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn',
                                    editScore: s,
                                  })
                                }
                                sx={{
                                  textTransform: 'none',
                                  p: 0,
                                  minWidth: 0,
                                  fontSize: 13,
                                  fontWeight: 500,
                                  justifyContent: 'flex-start',
                                  '&:hover': { textDecoration: 'underline' },
                                }}
                              >
                                GK: <b style={{ marginLeft: 4 }}>{judgeName}</b>
                              </Button>
                              <span>
                                <b>{Number(s.total_score).toFixed(1)}</b>/{TOTAL_MAX}
                              </span>
                            </Box>
                          )
                        })}
                      </Stack>
                    </Box>
                  )}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() =>
                      setDialog({
                        open: true,
                        teamId: f.team_id,
                        teamName: team?.name || f.team_id,
                        role: f.role === 'plaintiff' ? 'Nguyên đơn' : 'Bị đơn',
                        editScore: null,
                      })
                    }
                    sx={{ textTransform: 'none', fontWeight: 700 }}
                  >
                    Nhập phiếu chấm cho {team?.name}
                  </Button>
                </Paper>
              </Grid>
            )
          })}
        </Grid>
      </Paper>

      <FinalProxyDialog
        open={dialog.open}
        teamId={dialog.teamId}
        teamName={dialog.teamName}
        role={dialog.role}
        caseId={caseId}
        roundId={finalRoundId}
        judges={judges as any[]}
        editScore={dialog.editScore}
        onClose={() => setDialog((s) => ({ ...s, open: false }))}
        onSaved={() => {
          setDialog((s) => ({ ...s, open: false }))
          refetchScores()
        }}
      />
    </>
  )
}

interface DialogProps {
  open: boolean
  teamId: string
  teamName: string
  role: string
  caseId: string
  roundId: string
  judges: { id: string; full_name?: string | null; username?: string | null; email?: string | null }[]
  editScore: any | null
  onClose: () => void
  onSaved: () => void
}

function FinalProxyDialog({ open, teamId, teamName, role, caseId, roundId, judges, editScore, onClose, onSaved }: DialogProps) {
  const isEditMode = !!editScore
  const [judgeMode, setJudgeMode] = useState<'existing' | 'manual'>('existing')
  const [judgeId, setJudgeId] = useState('')
  const [judgeName, setJudgeName] = useState('')
  const [scores, setScores] = useState<Record<string, number | ''>>({})
  const [comments, setComments] = useState('')
  const [err, setErr] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [createProxy] = useCreateFinalProxyEntryMutation()
  const [updateProxy] = useUpdateFinalProxyEntryMutation()

  // 2026-05-27: pre-fill khi edit mode (load from editScore)
  useEffect(() => {
    if (open) {
      if (editScore) {
        // Edit: hydrate từ score record
        if (editScore.delegating_judge_id) {
          setJudgeMode('existing')
          setJudgeId(editScore.delegating_judge_id)
          setJudgeName('')
        } else {
          setJudgeMode('manual')
          setJudgeId('')
          setJudgeName(editScore.proxy_judge_name || '')
        }
        const init: Record<string, number | ''> = {}
        FINAL_CRITERIA.forEach((c) => {
          const v = (editScore.rubric_scores || {})[c.key]
          init[c.key] = typeof v === 'number' ? v : ''
        })
        setScores(init)
        setComments(editScore.comments || '')
      } else {
        // Create: reset
        setJudgeMode('existing')
        setJudgeId('')
        setJudgeName('')
        setScores({})
        setComments('')
      }
      setErr('')
    }
  }, [open, editScore])

  const total = useMemo(
    () =>
      FINAL_CRITERIA.reduce((sum, c) => {
        const v = scores[c.key]
        return sum + (typeof v === 'number' ? v : 0)
      }, 0),
    [scores],
  )
  const overflows = FINAL_CRITERIA.filter((c) => {
    const v = scores[c.key]
    return typeof v === 'number' && v > c.max_score
  })
  const missing = FINAL_CRITERIA.filter((c) => {
    const v = scores[c.key]
    return v == null || v === '' || Number.isNaN(v)
  })

  const judgeValid =
    judgeMode === 'existing' ? !!judgeId : !!judgeName.trim()

  const handleSubmit = async () => {
    setErr('')
    if (!judgeValid) {
      setErr('Vui lòng chọn GK hoặc nhập tên GK')
      return
    }
    if (missing.length > 0) {
      setErr(`Thiếu điểm cho ${missing.length}/${FINAL_CRITERIA.length} tiêu chí`)
      return
    }
    if (overflows.length > 0) {
      setErr(`Có ${overflows.length} tiêu chí vượt quá điểm tối đa`)
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        rubric_scores: Object.fromEntries(
          FINAL_CRITERIA.map((c) => [c.key, typeof scores[c.key] === 'number' ? (scores[c.key] as number) : 0]),
        ),
        delegating_judge_id: judgeMode === 'existing' ? judgeId : undefined,
        proxy_judge_name: judgeMode === 'manual' ? judgeName.trim() : undefined,
        comments: comments.trim() || undefined,
      }
      if (isEditMode && editScore?.id) {
        await updateProxy({ score_id: editScore.id, ...payload }).unwrap()
      } else {
        await createProxy({
          case_id: caseId,
          round_id: roundId,
          team_id: teamId,
          ...payload,
        }).unwrap()
      }
      // Reset form
      setJudgeId('')
      setJudgeName('')
      setScores({})
      setComments('')
      onSaved()
    } catch (e: any) {
      setErr(e?.data?.detail || e?.message || (isEditMode ? 'Cập nhật phiếu thất bại' : 'Lưu phiếu thất bại'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Assessment color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={800}>
              {isEditMode ? 'Sửa phiếu chấm — Vòng Chung kết' : 'Nhập phiếu chấm — Vòng Chung kết'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {role} — {teamName}
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert severity={isEditMode ? 'warning' : 'info'}>
            {isEditMode ? (
              <>
                Đang <b>sửa phiếu</b> đã nhập trước đó. Thay đổi sẽ ghi đè
                điểm hiện tại + cập nhật ranking. Audit log lưu lại.
              </>
            ) : (
              <>
                Phiếu admin nhập sẽ <b>submit luôn</b> (KHÔNG có draft). Đảm bảo
                điểm chính xác trước khi bấm "Lưu phiếu".
              </>
            )}
          </Alert>

          {/* GK selection */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Giám khảo chấm
            </Typography>
            <RadioGroup
              row
              value={judgeMode}
              onChange={(_, val) => setJudgeMode(val as 'existing' | 'manual')}
              sx={{ mb: 1.5 }}
            >
              <FormControlLabel value="existing" control={<Radio size="small" />} label="Chọn GK có tài khoản" />
              <FormControlLabel value="manual" control={<Radio size="small" />} label="Nhập tên GK (không có TK)" />
            </RadioGroup>
            {judgeMode === 'existing' ? (
              <FormControl fullWidth size="small">
                <InputLabel id="judge-select-label">Giám khảo</InputLabel>
                <Select
                  labelId="judge-select-label"
                  label="Giám khảo"
                  value={judgeId}
                  onChange={(e) => setJudgeId(e.target.value as string)}
                >
                  <MenuItem value=""><em>-- Chọn GK --</em></MenuItem>
                  {judges.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.full_name || u.username || u.email || u.id}
                      {u.email ? ` (${u.email})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Tên Giám khảo"
                placeholder="VD: Thẩm phán Nguyễn Văn A"
                value={judgeName}
                onChange={(e) => setJudgeName(e.target.value)}
                helperText="Hệ thống lưu tên này làm định danh GK; các phiếu cùng tên gộp chung khi aggregate."
              />
            )}
          </Paper>

          {/* 5 tiêu chí */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              5 tiêu chí FINAL_RUBRIC — Tổng tối đa <b>{TOTAL_MAX}đ</b>
            </Typography>
            <Stack spacing={1.5}>
              {FINAL_CRITERIA.map((c) => {
                const v = scores[c.key]
                const overflow = typeof v === 'number' && v > c.max_score
                return (
                  <Box key={c.key}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={700} color="primary">
                          {c.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {c.description}
                        </Typography>
                      </Box>
                      <TextField
                        type="number"
                        size="small"
                        label={`/ ${c.max_score}`}
                        value={v === '' || v == null ? '' : v}
                        onChange={(e) => {
                          const raw = e.target.value
                          const parsed = raw === '' ? '' : Math.max(0, parseFloat(raw) || 0)
                          setScores((prev) => ({ ...prev, [c.key]: parsed }))
                        }}
                        inputProps={{ min: 0, max: c.max_score, step: 0.5, style: { textAlign: 'right', fontWeight: 700, width: 70 } }}
                        error={overflow}
                        helperText={overflow ? `Tối đa ${c.max_score}` : ''}
                        sx={{ minWidth: 120 }}
                      />
                    </Stack>
                  </Box>
                )
              })}
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" fontWeight={800} color={overflows.length > 0 ? 'error' : 'primary'}>
              Tổng: {total.toFixed(2)} / {TOTAL_MAX}
            </Typography>
          </Paper>

          {/* Comments */}
          <TextField
            label="Nhận xét (không bắt buộc)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            multiline
            minRows={2}
            maxRows={5}
            fullWidth
            size="small"
          />

          {err && <Alert severity="error">{err}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Huỷ</Button>
        <Button
          variant="contained"
          color="primary"
          disabled={submitting || !judgeValid || missing.length > 0 || overflows.length > 0}
          onClick={handleSubmit}
        >
          {submitting ? <CircularProgress size={20} /> : (isEditMode ? 'Cập nhật phiếu' : 'Lưu phiếu (submit ngay)')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
